import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CiChips, VerificationBadge } from "@/components/Badges";
import { BottlePhotoControl, BottleThumb } from "@/components/BottlePhoto";
import { PageHead } from "@/pages/HistoryPage";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const USE_LABEL = { brows: "Brows", lips: "Lips", eyeliner: "Eyeliner", correction: "Toning / correction", areola: "Areola", scar: "Scar camouflage" };
export const usesOf = (p) => (p.uses?.length ? p.uses : p.area === "both" ? ["brows", "lips"] : p.area && p.area !== "unspecified" ? [p.area] : []);
export const usesLabel = (p) => usesOf(p).map((u) => USE_LABEL[u] || u).join(" & ") || "Use not verified";
const USE_OPTS = [["all", "All uses"], ...Object.entries(USE_LABEL), ["unspecified", "Use not verified"]];
const STATUS_OPTS = [["all", "All evidence levels"], ["verified_shade", "Ingredients documented"], ["partial", "Partially documented"], ["unverified", "Not yet verified (shade)"], ["name_only", "Not yet verified (name only)"]];

export function PigmentRow({ p, actions }) {
  const { user } = useAuth();
  const qc = useQueryClient();
  return (
    <div data-testid={`pigment-row-${p.id}`} className="grid gap-3 border-b border-[#E6DBCF] px-5 py-4 transition-colors hover:bg-[#FDF2F0]/60 md:grid-cols-[auto_1.4fr_1.4fr_1fr_auto] md:items-center">
      <BottleThumb fileId={p.image_file_id} alt={`${p.brand} ${p.shade} bottle`} testId={`pigment-photo-${p.id}`} />
      <div className="min-w-0">
        <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-[#A88B7D]">{[p.brand, p.line].filter(Boolean).join(" · ")} · <span className="text-[#9C6218]" data-testid={`pigment-use-${p.id}`}>{usesLabel(p)}</span></p>
        <p className="font-serif text-xl font-semibold text-[#2C1810]">{p.shade || <span className="italic text-[#A88B7D]">Line-level entry</span>}</p>
        {p.description && <p className="mt-0.5 line-clamp-2 text-xs text-[#6E5549]" data-testid={`pigment-description-${p.id}`}>{p.description.replace(/^Manufacturer description:\s*/, "")}</p>}
        {(p.sku || p.ig_code) && <p className="mt-0.5 text-[10px] text-[#A88B7D]">{[p.sku && `SKU ${p.sku}`, p.ig_code].filter(Boolean).join(" · ")}</p>}
        {p.status !== "active" && <span className="text-[10px] font-semibold uppercase text-[#8C2A2A]">{p.status}</span>}
        {user?.role === "admin" && (
          <div className="mt-2"><BottlePhotoControl fileId={p.image_file_id} endpoint={`/admin/pigments/${p.id}/photo`} onChange={() => qc.invalidateQueries({ queryKey: ["pigments"] })} testId={`pigment-photo-${p.id}`} /></div>
        )}
      </div>
      <div className="space-y-1.5">
        <CiChips ci={p.ci_numbers} />
        <p className="text-[11px] italic text-[#6E5549]">{p.formulation_category}</p>
      </div>
      <div className="space-y-1.5">
        <VerificationBadge status={p.verification_status} testId={`pigment-verification-${p.id}`} />
        <p className="line-clamp-3 text-[11px] text-[#6E5549]">{p.source_note}</p>
        <p className="text-[10px] text-[#A88B7D]">Reviewed {p.last_reviewed || "—"}</p>
      </div>
      <div className="flex items-center gap-2">
        {p.source_url && <a href={p.source_url} target="_blank" rel="noreferrer" data-testid={`pigment-source-${p.id}`} className="inline-flex items-center gap-1 text-xs text-[#9C6218] hover:underline">Source <ExternalLink size={12} /></a>}
        {actions}
      </div>
    </div>
  );
}

export function usePigmentFilter(data) {
  const [q, setQ] = useState("");
  const [brand, setBrand] = useState("all");
  const [status, setStatus] = useState("all");
  const [use, setUse] = useState("all");
  const brands = useMemo(() => [...new Set(data.map((p) => p.brand))].sort(), [data]);
  const list = useMemo(() => {
    const s = q.toLowerCase();
    return data.filter((p) =>
      (brand === "all" || p.brand === brand) && (status === "all" || p.verification_status === status) &&
      (use === "all" || (use === "unspecified" ? !usesOf(p).length : usesOf(p).includes(use))) &&
      `${p.brand} ${p.line} ${p.shade} ${p.sku || ""} ${p.ci_numbers.join(" ")}`.toLowerCase().includes(s));
  }, [data, q, brand, status, use]);
  const controls = (
    <div className="flex flex-col gap-3 sm:flex-row">
      <Input data-testid="pigment-search-input" placeholder="Search shade, line, brand or CI number…" value={q} onChange={(e) => setQ(e.target.value)} className="h-11 flex-1 rounded-full border-[#E6DBCF] bg-[#FDFBF7] px-5 focus-visible:ring-[#C5A059]" />
      <Select value={brand} onValueChange={setBrand}>
        <SelectTrigger data-testid="pigment-brand-filter" className="h-11 rounded-full border-[#E6DBCF] bg-[#FDFBF7] sm:w-48"><SelectValue /></SelectTrigger>
        <SelectContent><SelectItem value="all">All brands</SelectItem>{brands.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={use} onValueChange={setUse}>
        <SelectTrigger data-testid="pigment-use-filter" className="h-11 rounded-full border-[#E6DBCF] bg-[#FDFBF7] sm:w-48"><SelectValue /></SelectTrigger>
        <SelectContent>{USE_OPTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
      </Select>
      <Select value={status} onValueChange={setStatus}>
        <SelectTrigger data-testid="pigment-status-filter" className="h-11 rounded-full border-[#E6DBCF] bg-[#FDFBF7] sm:w-56"><SelectValue /></SelectTrigger>
        <SelectContent>{STATUS_OPTS.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
      </Select>
    </div>
  );
  return { list, controls };
}

export default function LibraryPage() {
  const { data = [], isLoading } = useQuery({ queryKey: ["pigments"], queryFn: async () => (await api.get("/pigments")).data });
  const { list, controls } = usePigmentFilter(data);
  return (
    <div className="mx-auto max-w-6xl px-6 py-10 sm:px-10 lg:py-14">
      <PageHead eyebrow="Knowledge base" title="Pigment Library" sub="Curated research records, including the October 2026 verified database for brows, lips, eyeliner, correctors and areola — not live-updating and not all verified. CI presence never reveals concentration, particle size or healed outcome. Carbon black (CI 77266) is shown in dark chips." />
      <div className="mt-8">{controls}</div>
      <p className="mt-4 text-xs text-[#A88B7D]" data-testid="pigment-count">{list.length} {list.length === 1 ? "record" : "records"}</p>
      <div className="mt-3 overflow-hidden rounded-2xl border border-[#E6DBCF] bg-[#FDFBF7]" data-testid="pigment-list">
        {list.map((p) => <PigmentRow key={p.id} p={p} />)}
        {!isLoading && !list.length && <p className="p-8 text-sm italic text-[#A88B7D]">No records match.</p>}
      </div>
    </div>
  );
}
